/**
 * CommentTemplateCard Component
 *
 * Displays a single comment template in a card format with name, content preview,
 * and action buttons for editing and deleting.
 *
 * Layout:
 * - Card header with template name and edit/delete action buttons
 * - Content area with 2-line truncation by default
 * - "See more/less" toggle button for expanding/collapsing content
 *
 * @param template - The comment template object to display
 * @param onEdit - Callback function when edit button is clicked
 * @param onDelete - Callback function when delete button is clicked
 *
 * @example
 * ```tsx
 * <CommentTemplateCard
 *   template={template}
 *   onEdit={(template) => handleEdit(template)}
 *   onDelete={(template) => handleDelete(template)}
 * />
 * ```
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardPenLine, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { markdownToPlainTextWithMentions } from '@/features/view-tfl-uploaded/utils/slateUtils'
import { ResponseCommentTemplate } from '@/schema/commentTemplate'
import { cn } from '@/utils'

interface CommentTemplateCardProps {
  template: ResponseCommentTemplate
  onEdit: (template: ResponseCommentTemplate) => void
  onDelete: (template: ResponseCommentTemplate) => void
}

export const CommentTemplateCard: React.FC<CommentTemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [shouldShowToggle, setShouldShowToggle] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const { contentElements } = useMemo(() => {
    const elements = markdownToPlainTextWithMentions(template.content)
    return {
      contentElements: elements,
    }
  }, [template.content])

  useEffect(() => {
    // Reset state when content changes
    setIsExpanded(false)
    setShouldShowToggle(false)

    const checkOverflow = () => {
      if (contentRef.current) {
        const element = contentRef.current
        const isOverflowing = element.scrollHeight > element.clientHeight
        setShouldShowToggle(isOverflowing)
      }
    }

    // Check overflow immediately if in test environment, otherwise use timeout
    const isTestEnv =
      typeof process !== 'undefined' && process.env.NODE_ENV === 'test'
    if (isTestEnv) {
      checkOverflow()
    } else {
      const timeoutId = setTimeout(checkOverflow, 0)
      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [contentElements])

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  return (
    <div className="py-4 pl-6 pr-4 mr-2 transition-shadow bg-white border border-solid rounded-lg shadow-sm border-gray-light-200 hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="m-0 text-base font-semibold text-primary-900">
            {template.name}
          </h3>
          <div className="text-sm text-gray-light-600">
            <div
              ref={contentRef}
              className={cn('break-words', !isExpanded && 'line-clamp-2')}
            >
              {contentElements}
            </div>
            {shouldShowToggle && (
              <Button
                variant="text"
                onClick={handleToggleExpand}
                className="p-0 mt-2 text-sm font-normal text-blue-600"
              >
                {isExpanded
                  ? t('commentTemplate.seeLess')
                  : t('commentTemplate.seeMore')}
              </Button>
            )}
          </div>
        </div>
        <div className="flex shrink-0">
          <Button
            variant="text"
            onClick={() => onEdit(template)}
            aria-label={t('common.edit')}
            className="p-2 text-gray-light-500 hover:text-gray-light-900"
          >
            <ClipboardPenLine className="w-5 h-5" />
          </Button>
          <Button
            variant="text"
            onClick={() => onDelete(template)}
            aria-label={t('common.delete')}
            className="p-2 text-gray-light-500 hover:text-red-600"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
